import { useEffect, useMemo, useRef, useState } from 'react';
import type { RouteDto } from '@geotracker/contracts';
import { cn } from '@/lib/utils';
import { formatClock, formatSpeed } from '@/lib/format';

/**
 * Route replay.
 *
 * Entirely client-side: every point carries `t`, milliseconds from the first
 * fix, so scrubbing and speed changes are interpolation over an array the
 * browser already holds. Nothing here touches the server, which is what makes
 * dragging the scrubber feel like dragging rather than like waiting.
 *
 * Playback is expressed in minutes of the working day per second of watching,
 * not as "2×". A tracking day is eight hours; a multiplier of real time is
 * meaningless at that scale, and "5 min/s" tells you a shift takes a minute and
 * a half to watch.
 */

const SPEEDS = [
  { label: '1 min/s', msPerSecond: 60_000 },
  { label: '5 min/s', msPerSecond: 300_000 },
  { label: '20 min/s', msPerSecond: 1_200_000 },
] as const;

const TICK_MS = 100;

export interface ReplayState {
  index: number | null;
  elapsed: number;
}

interface Props {
  route: RouteDto;
  /** Set by the timeline when a band is clicked; scrubs to that instant. */
  seekTo: string | null;
  onIndexChange: (index: number | null) => void;
}

export function Replay({ route, seekTo, onIndexChange }: Props) {
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [elapsed, setElapsed] = useState(0);
  const [active, setActive] = useState(false);

  const duration = route.points[route.points.length - 1]?.t ?? 0;
  const baseMs = useMemo(
    () => (route.points[0] ? new Date(route.points[0].recordedAt).getTime() : 0),
    [route.points],
  );

  // A ref, not state: the interval closes over it, and putting `elapsed` in the
  // dependency list would tear the timer down and rebuild it ten times a second.
  const elapsedRef = useRef(0);
  elapsedRef.current = elapsed;

  const index = useMemo(() => {
    if (!active || route.points.length === 0) return null;
    // Binary search: a day can be several thousand fixes and this runs on every
    // animation tick.
    let low = 0;
    let high = route.points.length - 1;
    while (low < high) {
      const mid = (low + high + 1) >> 1;
      if ((route.points[mid]?.t ?? 0) <= elapsed) low = mid;
      else high = mid - 1;
    }
    return low;
  }, [active, elapsed, route.points]);

  useEffect(() => {
    onIndexChange(index);
  }, [index, onIndexChange]);

  // Selecting another employee or another day invalidates the play head.
  useEffect(() => {
    setPlaying(false);
    setActive(false);
    setElapsed(0);
  }, [route.employeeId, route.date]);

  useEffect(() => {
    if (seekTo === null) return;
    const target = new Date(seekTo).getTime() - baseMs;
    setActive(true);
    setPlaying(false);
    setElapsed(Math.max(0, Math.min(duration, target)));
  }, [seekTo, baseMs, duration]);

  useEffect(() => {
    if (!playing) return;

    const step = (SPEEDS[speed]?.msPerSecond ?? 300_000) * (TICK_MS / 1000);
    const timer = setInterval(() => {
      const next = elapsedRef.current + step;
      if (next >= duration) {
        setElapsed(duration);
        setPlaying(false);
        return;
      }
      setElapsed(next);
    }, TICK_MS);

    return () => clearInterval(timer);
  }, [playing, speed, duration]);

  if (route.points.length < 2) return null;

  const current = index === null ? null : route.points[index];

  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-border px-3 py-2">
      <button
        type="button"
        onClick={() => {
          setActive(true);
          setPlaying((p) => !p);
          if (elapsed >= duration) setElapsed(0);
        }}
        className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90"
        aria-label={playing ? 'Pause replay' : 'Play replay'}
      >
        {playing ? (
          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
            <path d="M8 5.5v13l11-6.5z" />
          </svg>
        )}
      </button>

      <input
        type="range"
        min={0}
        max={duration}
        step={1000}
        value={elapsed}
        onChange={(e) => {
          setActive(true);
          setElapsed(Number(e.target.value));
        }}
        aria-label="Replay position"
        className="h-1 min-w-[8rem] flex-1 cursor-pointer appearance-none rounded-full bg-muted accent-primary"
      />

      <div className="shrink-0 text-2xs tabular-nums text-muted-foreground">
        {current ? (
          <>
            <span className="font-medium text-foreground">
              {formatClock(current.recordedAt, route.timezone)}
            </span>
            {current.spd !== null ? <> · {formatSpeed(current.spd)}</> : null}
          </>
        ) : (
          <>
            {formatClock(route.summary.firstPointAt, route.timezone)} –{' '}
            {formatClock(route.summary.lastPointAt, route.timezone)}
          </>
        )}
      </div>

      <div className="flex shrink-0 gap-1">
        {SPEEDS.map((s, i) => (
          <button
            key={s.label}
            type="button"
            onClick={() => setSpeed(i)}
            className={cn(
              'rounded-md px-1.5 py-0.5 text-2xs font-medium transition-colors',
              speed === i
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {active ? (
        <button
          type="button"
          onClick={() => {
            setPlaying(false);
            setActive(false);
            setElapsed(0);
          }}
          className="shrink-0 text-2xs text-muted-foreground hover:text-foreground"
        >
          Exit replay
        </button>
      ) : null}
    </div>
  );
}
